import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import ptBR from './locales/pt-BR.json'
import en from './locales/en.json'
import es from './locales/es.json'
import pwaPtBR from './locales/pwa/pt-BR.json'
import pwaEn from './locales/pwa/en.json'
import pwaEs from './locales/pwa/es.json'

export const SUPPORTED_LOCALES = ['pt-BR', 'en', 'es'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]
export const LOCALE_STORAGE_KEY = 'lumora.locale'

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/** pt* -> pt-BR, es* -> es, anything else -> en. */
export function normalizeLocale(raw: string | null | undefined): AppLocale {
  const value = (raw ?? '').toLowerCase()
  if (value.startsWith('pt')) return 'pt-BR'
  if (value.startsWith('es')) return 'es'
  return 'en'
}

export function detectInitialLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (isAppLocale(stored)) return stored
  } catch {
    /* ignore */
  }
  const candidates = typeof navigator === 'undefined' ? [] : (navigator.languages?.length ? navigator.languages : [navigator.language])
  for (const candidate of candidates) {
    const normalized = normalizeLocale(candidate)
    if (candidate && (normalized !== 'en' || candidate.toLowerCase().startsWith('en'))) return normalized
  }
  return 'en'
}

export function syncDocumentLanguage(locale: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = locale
}

/** Changes the UI language, remembers it locally and updates <html lang>. */
export async function setLocale(locale: AppLocale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    /* ignore */
  }
  await i18n.changeLanguage(locale)
}

void i18n.use(initReactI18next).init({
  resources: {
    'pt-BR': { translation: ptBR, pwa: pwaPtBR },
    en: { translation: en, pwa: pwaEn },
    es: { translation: es, pwa: pwaEs },
  },
  ns: ['translation', 'pwa'],
  defaultNS: 'translation',
  lng: detectInitialLocale(),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LOCALES],
  interpolation: { escapeValue: false },
  returnNull: false,
})

syncDocumentLanguage(i18n.language)
i18n.on('languageChanged', syncDocumentLanguage)

export default i18n
